import { useState } from 'react';
import { ArrowLeft, Heart, MessageCircle, Share2, MoreVertical, Image as ImageIcon, Smile, Send, TrendingUp, Users, Award, Plus, Filter, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { useTheme, themes } from './ThemeProvider';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface CommunityLoggedInProps {
  onBack: () => void;
  userName?: string;
  userAvatar?: string;
}

interface Post {
  id: number;
  author: {
    name: string;
    avatar: string;
    verified: boolean;
    location: string;
  };
  content: string;
  image?: string;
  likes: number;
  comments: number;
  shares: number;
  timestamp: string;
  category: 'success-story' | 'advice' | 'question' | 'experience' | 'general';
  liked?: boolean;
}

const samplePosts: Post[] = [
  {
    id: 1,
    author: {
      name: "Priya & Rahul",
      avatar: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&h=100&fit=crop",
      verified: true,
      location: "Mumbai, Maharashtra"
    },
    content: "We found each other on Atootbandan 6 months ago, and today we're getting married! Thank you for helping us find our perfect match. The AI recommendations were spot-on! 💕 #AtootbandanSuccess",
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop",
    likes: 1245,
    comments: 89,
    shares: 234,
    timestamp: "2 hours ago",
    category: "success-story",
    liked: false
  },
  {
    id: 2,
    author: {
      name: "Anjali Sharma",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
      verified: true,
      location: "Delhi, India"
    },
    content: "Quick tip for everyone: Make sure to add recent photos and be honest in your profile. Authenticity attracts the right matches! I got 50+ quality matches in just one week. 🌟",
    likes: 892,
    comments: 45,
    shares: 156,
    timestamp: "5 hours ago",
    category: "advice"
  },
  {
    id: 3,
    author: {
      name: "Amit Patel",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop",
      verified: false,
      location: "Bangalore, Karnataka"
    },
    content: "How do you guys handle the first conversation? I'm getting matches but struggling to start meaningful conversations. Any tips would be appreciated! 🤔",
    likes: 234,
    comments: 67,
    shares: 12,
    timestamp: "8 hours ago",
    category: "question"
  }
];

const trendingTopics = [
  { tag: 'FirstDateIdeas', posts: 1234 },
  { tag: 'SuccessStories', posts: 5678 },
  { tag: 'ProfileTips', posts: 890 },
  { tag: 'RelationshipGoals', posts: 2345 },
  { tag: 'WeddingPlanning', posts: 1567 }
];

export default function CommunityLoggedIn({ onBack, userName = "You", userAvatar }: CommunityLoggedInProps) {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const [posts, setPosts] = useState<Post[]>(samplePosts);
  const [newPost, setNewPost] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postCategory, setPostCategory] = useState('general');
  const [activeTab, setActiveTab] = useState('feed');

  const categories = [
    { id: 'all', label: 'All Posts', icon: '📰' },
    { id: 'success-story', label: 'Success Stories', icon: '💑' },
    { id: 'advice', label: 'Advice', icon: '💡' },
    { id: 'question', label: 'Questions', icon: '❓' },
    { id: 'experience', label: 'Experiences', icon: '⭐' },
    { id: 'general', label: 'General', icon: '💬' }
  ];

  const handleLike = (postId: number) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          liked: !post.liked,
          likes: post.liked ? post.likes - 1 : post.likes + 1
        };
      }
      return post;
    }));
  };

  const handleShare = (postId: number) => {
    toast.success('Post link copied to clipboard!');
  };

  const handleCreatePost = () => {
    if (!newPost.trim()) {
      toast.error('Please write something to post');
      return;
    }

    const post: Post = {
      id: posts.length + 1,
      author: {
        name: userName,
        avatar: userAvatar || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
        verified: false,
        location: "Your Location"
      },
      content: newPost,
      likes: 0,
      comments: 0,
      shares: 0,
      timestamp: "Just now",
      category: postCategory as any,
      liked: false
    };

    setPosts([post, ...posts]);
    setNewPost('');
    setShowCreatePost(false);
    toast.success('Post created successfully!');
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header */}
      <div 
        className="sticky top-0 z-50 border-b shadow-sm"
        style={{ 
          background: currentTheme.primaryGradient,
          borderBottomColor: currentTheme.accent 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-white">Community</h1>
                <p className="text-xs text-white/80">Connect with fellow members</p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowCreatePost(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6">
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="feed">Feed</TabsTrigger>
                <TabsTrigger value="trending">Trending</TabsTrigger>
                <TabsTrigger value="my-posts">My Posts</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Category Filter */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                  <Filter className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  {categories.map((category) => (
                    <Button
                      key={category.id}
                      variant={selectedCategory === category.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory(category.id)}
                      style={selectedCategory === category.id ? { backgroundColor: currentTheme.accent } : {}}
                      className="whitespace-nowrap"
                    >
                      <span className="mr-1">{category.icon}</span>
                      {category.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Posts Feed */}
            <div className="space-y-4">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={post.author.avatar} />
                          <AvatarFallback>{post.author.name[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{post.author.name}</h4>
                            {post.author.verified && (
                              <Badge variant="secondary" className="h-5 px-1.5 bg-blue-100 text-blue-700">
                                ✓ Verified
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {post.author.location} • {post.timestamp}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>Save Post</DropdownMenuItem>
                          <DropdownMenuItem>Report Post</DropdownMenuItem>
                          <DropdownMenuItem>Hide Post</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="whitespace-pre-wrap">{post.content}</p>
                    {post.image && (
                      <img 
                        src={post.image} 
                        alt="Post" 
                        className="w-full rounded-lg object-cover max-h-96"
                      />
                    )}
                    <div className="flex items-center gap-2 pt-2">
                      <Badge variant="outline" className="text-xs">
                        {categories.find(c => c.id === post.category)?.icon} {categories.find(c => c.id === post.category)?.label}
                      </Badge>
                    </div>
                  </CardContent>
                  <CardFooter className="border-t pt-4">
                    <div className="flex items-center justify-around w-full">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLike(post.id)}
                        className={post.liked ? 'text-pink-600' : ''}
                      >
                        <Heart className={`h-4 w-4 mr-2 ${post.liked ? 'fill-current' : ''}`} />
                        {post.likes}
                      </Button>
                      <Button variant="ghost" size="sm">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        {post.comments}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleShare(post.id)}>
                        <Share2 className="h-4 w-4 mr-2" />
                        {post.shares}
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No posts found in this category</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => setSelectedCategory('all')}
                    style={{ backgroundColor: currentTheme.accent }}
                  >
                    View All Posts
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* Trending Topics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-5 w-5" style={{ color: currentTheme.accent }} />
                  Trending Topics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {trendingTopics.map((topic, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <div>
                      <p className="font-medium text-sm">#{topic.tag}</p>
                      <p className="text-xs text-gray-500">{topic.posts.toLocaleString()} posts</p>
                    </div>
                    <Badge variant="outline">{index + 1}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Community Guidelines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Award className="h-5 w-5" style={{ color: currentTheme.accent }} />
                  Community Guidelines
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-gray-600">
                <p>• Be respectful and kind to all members</p>
                <p>• No spam or promotional content</p>
                <p>• Share authentic experiences</p>
                <p>• Protect your privacy and others'</p>
                <p>• Report inappropriate content</p>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card style={{ background: currentTheme.primaryGradient }} className="text-white">
              <CardHeader>
                <CardTitle className="text-base">Community Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="opacity-90">Total Members</span>
                  <span className="font-semibold">50,000+</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="opacity-90">Success Stories</span>
                  <span className="font-semibold">5,600+</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="opacity-90">Posts Today</span>
                  <span className="font-semibold">1,200+</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}